// R268eeee · Feb 2026 — Self-service Security page.
// Renders the current user's password-reset audit trail + recent
// login sessions so users can spot suspicious activity. Both endpoints
// are read-only and scoped to the caller's own `user_id`.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Shield, KeyRound, Monitor, RefreshCw, Clock, MapPin, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { const d = new Date(iso); return d.toLocaleString(); }
  catch { return iso.slice(0, 19).replace("T", " "); }
};
const shortUA = (ua) => {
  if (!ua) return "unknown";
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/([\d.]+)/i);
  const os = ua.match(/(Windows|Mac|Linux|Android|iPhone|iPad|iOS)/i);
  return [os?.[1], m?.[1]].filter(Boolean).join(" · ") || "unknown";
};

export default function AccountSecurity() {
  const [resets, setResets] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/me/security/reset-events"),
        api.get("/me/security/sessions"),
      ]);
      setResets(r1.data?.events || []);
      setSessions(r2.data?.sessions || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load security activity")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const failedLogins = sessions.filter((s) => s.success === false).length;
  const distinctIps = new Set(sessions.map((s) => s.ip).filter(Boolean)).size;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8" data-testid="account-security-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Account</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" /> Security
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            See every password-reset request and recent sign-in on your account. Anything unfamiliar? Reset your password and contact your workspace admin.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none" onClick={load} disabled={busy} data-testid="security-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Link to="/forgot-password">
            <Button size="sm" className="rounded-none" data-testid="security-reset-pw-cta">
              <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Reset my password
            </Button>
          </Link>
        </div>
      </header>

      {/* Simple at-a-glance stats */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        <StatCard label="Password resets" value={resets.length} icon={KeyRound} testid="security-stat-resets" />
        <StatCard label="Recent sign-ins" value={sessions.filter((s) => s.success !== false).length} icon={Monitor} testid="security-stat-sessions" />
        <StatCard label="Failed attempts" value={failedLogins} icon={Shield} tone={failedLogins > 3 ? "danger" : "muted"} testid="security-stat-failed" />
      </div>

      {/* Password reset audit */}
      <section data-testid="security-reset-section">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
          <KeyRound className="w-4 h-4" /> Password reset events
        </h2>
        {busy && resets.length === 0
          ? <div className="text-sm text-muted-foreground italic p-6 text-center">Loading…</div>
          : resets.length === 0
            ? <div className="border border-dashed border-border text-sm text-muted-foreground italic p-6 text-center">
                No password-reset requests yet. Reset links appear here as soon as they're sent.
              </div>
            : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-xs border-collapse report-grid min-w-[720px]">
                  <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-2 py-1.5">Requested</th>
                      <th className="text-left px-2 py-1.5">Requested from</th>
                      <th className="text-left px-2 py-1.5">Completed</th>
                      <th className="text-left px-2 py-1.5">Completed from</th>
                      <th className="text-left px-2 py-1.5">Origin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resets.map((r) => {
                      const mismatch = r.used_ip && r.created_ip && r.used_ip !== r.created_ip;
                      return (
                        <tr key={r.id} className={mismatch ? "bg-rose-500/5" : ""} data-testid={`security-reset-row-${r.id}`}>
                          <td className="px-2 py-1"><Clock className="w-3 h-3 inline mr-1 text-muted-foreground" />{fmtDate(r.created_at)}</td>
                          <td className="px-2 py-1"><MapPin className="w-3 h-3 inline mr-1 text-muted-foreground" />{r.created_ip || "—"} <span className="text-muted-foreground">· {shortUA(r.created_ua)}</span></td>
                          <td className="px-2 py-1">{r.used_at ? fmtDate(r.used_at) : <span className="text-muted-foreground">not used</span>}</td>
                          <td className="px-2 py-1">{r.used_ip ? (<><MapPin className="w-3 h-3 inline mr-1 text-muted-foreground" />{r.used_ip} <span className="text-muted-foreground">· {shortUA(r.used_ua)}</span></>) : <span className="text-muted-foreground">—</span>}
                          {mismatch && <span className="ml-1 text-rose-600 text-[10px] uppercase tracking-widest">IP mismatch</span>}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">{r.created_by ? "admin-triggered" : "self-serve"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
      </section>

      {/* Recent sign-ins */}
      <section data-testid="security-sessions-section">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
          <Monitor className="w-4 h-4" /> Recent sign-ins
          {distinctIps > 1 && (
            <span className="text-[10px] normal-case tracking-normal text-muted-foreground font-normal">· {distinctIps} distinct IPs</span>
          )}
        </h2>
        {busy && sessions.length === 0
          ? <div className="text-sm text-muted-foreground italic p-6 text-center">Loading…</div>
          : sessions.length === 0
            ? <div className="border border-dashed border-border text-sm text-muted-foreground italic p-6 text-center">
                No sign-ins recorded yet. Log in again and you'll see the event here.
              </div>
            : (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-xs border-collapse report-grid min-w-[640px]">
                  <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-2 py-1.5">Signed in</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                      <th className="text-left px-2 py-1.5">IP</th>
                      <th className="text-left px-2 py-1.5">Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s, i) => (
                      <tr key={i} className={s.success === false ? "bg-rose-500/5" : ""} data-testid={`security-session-row-${i}`}>
                        <td className="px-2 py-1"><Clock className="w-3 h-3 inline mr-1 text-muted-foreground" />{fmtDate(s.created_at)}</td>
                        <td className="px-2 py-1">
                          {s.success === false
                            ? <span className="text-rose-600 uppercase text-[10px] tracking-widest">Failed</span>
                            : <span className="text-emerald-600 uppercase text-[10px] tracking-widest">Success</span>}
                        </td>
                        <td className="px-2 py-1">{s.ip || "—"}</td>
                        <td className="px-2 py-1 text-muted-foreground">{shortUA(s.user_agent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </section>

      {/* Footer helper */}
      <div className="border-t border-border pt-4 text-xs text-muted-foreground flex items-center justify-between gap-2">
        <span>Seeing something unexpected? Reset your password immediately.</span>
        <Link to="/app/profile" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="security-back-profile">
          Back to profile <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "muted", testid }) {
  const toneCls = tone === "danger"
    ? "border-rose-500/40 bg-rose-500/5 text-rose-700"
    : "border-border bg-muted/20 text-foreground";
  return (
    <div className={`border ${toneCls} px-3 py-2`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-xl font-semibold font-mono">{value}</div>
    </div>
  );
}
